'use client'
/***
 *
 * @file components/MenuItem/index.tsx
 * @description MenuItem component will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Ingka Centres
 * @since 13 Feb 2024
 *
 */

import React from "react";
// import "../TopMenu/TopMenu.scss";
// import styles from "../TopMenu/TopMenu.module.scss";
import { StyledLi } from "@/shared/StyledComponents";
import MenuItemList from "../MenuList";
import CustomLink from "@/components/CustomLink";
import { usePathname } from "next/navigation";
import { getUrl } from "@/helpers/utils.helper";
import ScrollLink from "@/components/ScrollLink";
import { getDefaultLocale, getLocale } from "@/config/app.config";

/**
 * type of props to pass inside MenuItem functional component
 */
type T_Props = {
  /** id content to be displayed inside the Top menu */
  id?: any,

  /** data content to be displayed inside the Top menu */
  data?: any,

  /** parentIndex content to be displayed inside the Top menu */
  parentIndex?: any,

  /** menuIndex content to be displayed inside the Top menu */
  menuIndex?: any,

  /** CSS class to be added to the MenuItem component */
  className?: string,

  /** custom CSS styles to be passed in the style html attribute inside MenuItem */
  customStyles?: any,

  /** to set the alignment for menu items */
  menuAlignment?: string,

  locale?: string,

  /** action to be performed on click of the menu item */
  action?: () => void
}

/**
 * Name: Top MenuItem
 * 
 * Description: A functional component to display the Navbar throughout the application window
 * 
 * @param param0 object containing the following properties:
 *                - data: content to be displayed in the Top MenuItem component
 *                - className: CSS class to be applied to the MenuItem component
 *                - customStyles: CSS styles to be applied to the MenuItem component 
 * 
 * @example T_Props
 * ```
 * type T_Props = {
 *      id?: any,               // optional
 *      data?: any,             // optional
 *      parentIndex?: any,      // optional
 *      menuIndex?: any,        // optional
 *      className?: string,     // optional
 *      customStyles?: any,     // optional
 *      menuAlignment?: string  // optional
 * }
 * ```
 * 
 * @returns html element navbar to be rendered on the UI
 */
const MenuItem = ({ data = {}, menuIndex = 0, parentIndex = 0, locale = getLocale(), action }: T_Props) => {
  const pathname = usePathname();
  const localizedHomeHref = locale && locale !== getDefaultLocale() ? `/${locale}` : "/";

  /**
   * Name: MenuItem
   * 
   * Description: a functional component that accepts the list of menu items from Contentful API and returns html elements to display each menu item as a respectice list item
   * 
   * @param param0 object containing the following properties:
  *                - menuList: menu items to be displayed inside the MenuItem navbar component
  *                - parentIndex: index of the parent component of the MenuItem
   * @returns an html list of menu items to be displayed within MenuItem
   */

  return (
    <StyledLi data-testid="MenuItem" key={`menu-${parentIndex}-${menuIndex}-${data?.content?.id || data?.id}`} className={`nav-item ${pathname === `${data?.content?.url || data?.content?.slug || "/"}` ? 'active' : ''}`} onClick={action}>
      {(data?.content?.externalUrl || data?.content?.slug) ? (
        <CustomLink id={data?.id} data={data}  cssClass="nav-link !text-interactive-nav-link hover:text-interactive-nav-link !mx-menu-link-margin" target={data?.content?.openInNewTab === true ? "_blank" : ""} rel="noopener noreferrer" role="link" href={(data?.content?.externalUrl || data?.content?.slug) || localizedHomeHref} locale={locale} />
      ) : (
        data?.content?.hasOwnProperty("pages") && data?.content?.pages?.length > 0 ? (
          <>
            <CustomLink cssClass="nav-link !text-interactive-nav-link hover:text-interactive-nav-link dropdown-toggle !mx-menu-link-margin" data={data} href="#" locale={locale} id={data?.content?.id} role="button" data-toggle="dropdown" aria-haspopup={true} aria-expanded={false} />
            <MenuItemList className="dropdown-menu" data={data?.content?.pages} parentIndex={menuIndex} ariaLabelledby={data?.content?.id} action={action}/>
          </>
        ) : (
          <ScrollLink id={data?.id} data={data} menuIndex={menuIndex} pathName={pathname} className="nav-link !text-interactive-nav-link hover:!text-interactive-nav-link !mx-menu-link-margin" href={getUrl({url: (data?.content?.url || data?.content?.slug), queryParams: {t: data?.content?.tagName || "", ct: data?.content?.contentType || ""}})} locale={locale} />
        )
      )}
    </StyledLi>
  );
};

export default MenuItem;
